import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { AccordionItem as AccordionItem$1, AccordionHeader, AccordionTrigger as AccordionTrigger$1, AccordionContent as AccordionContent$1, Accordion as Accordion$1 } from '../../vendor/@radix-ui/react-accordion/dist/index.mjs';
import * as React from 'react';
import { LineIcon } from './icons/LineIcon.js';
import { useDevice } from '../../contexts/DeviceContext.js';
import { cn } from '../../lib/utils.js';

const AccordionContext = React.createContext({
  direction: "top",
  variants: "default",
  section: "gap"
});
const Accordion = ({
  direction = "top",
  variants = "default",
  section,
  ...props
}) => {
  return /* @__PURE__ */ jsx(AccordionContext.Provider, { value: { direction, variants, section }, children: /* @__PURE__ */ jsx(
    Accordion$1,
    {
      className: cn(
        "w-full overflow-hidden flex flex-col border-none",
        variants === "default" && " overflow-hidden",
        section === "gap" ? "gap-3" : ""
      ),
      ...props
    }
  ) });
};
const AccordionItem = React.forwardRef(({ className, ...props }, ref) => {
  const { section } = React.useContext(AccordionContext);
  return /* @__PURE__ */ jsx(
    AccordionItem$1,
    {
      ref,
      className: cn(
        "flex flex-col overflow-hidden border border-basic-04 rounded-[12px]",
        section === "group" && "border-b-0 [&:last-child]:border-b rounded-[0px] [&:first-child]:rounded-[12px_12px_0px_0px] [&:last-child]:rounded-[0px_0px_12px_12px]",
        className
      ),
      ...props
    }
  );
});
AccordionItem.displayName = "AccordionItem";
const AccordionTrigger = React.forwardRef(({ isChangeTitle, openTitle, closeTitle, className, children, isTarget = false, onTargetClick, ...props }, ref) => {
  const { isDesktop } = useDevice();
  const { direction, variants } = React.useContext(AccordionContext);
  const arrowClassName = cn("arrow");
  const renderHeaderIcon = () => {
    switch (variants) {
      case "under":
        return /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx(
            LineIcon,
            {
              icoColor: "bg-icon-basic-03",
              icoName: "ico-plus-circle",
              type: "xs",
              className: "open-title"
            }
          ),
          /* @__PURE__ */ jsx(
            LineIcon,
            {
              icoColor: "bg-icon-basic-03",
              icoName: "ico-minus-circle",
              type: "xs",
              className: "close-title"
            }
          )
        ] });
      default:
        return /* @__PURE__ */ jsx(
          LineIcon,
          {
            icoColor: "bg-icon-basic-01",
            icoName: "ico-caret-down",
            className: arrowClassName
          }
        );
    }
  };
  return /* @__PURE__ */ jsx(AccordionHeader, { asChild: true, children: /* @__PURE__ */ jsx("div", { className: cn("flex", direction === "bottom" ? "order-2" : "order-1"), children: /* @__PURE__ */ jsxs(
    AccordionTrigger$1,
    {
      ref,
      className: cn(
        "relative z-10 flex flex-1 gap-4 items-center p-5 pr-4 justify-between body-lg font-weight-bold transition-all hover:underline text-left data-[state=open]:text-primary-04 data-[state=open]:rounded-bl-none data-[state=open]:rounded-br-none !no-underline [&[data-state=open]&_.open-title]:hidden [&[data-state=closed]&_.close-title]:hidden [&[data-state=open]>.header>.arrow]:rotate-180 [&[data-state=open]>.header>.arrow]:bg-icon-primary-04 data-[state=closed]:mb-0",
        variants === "under" ? "border-none justify-center h-[26px] body-md font-weight-medium p-0 mb-4" : "body-lg bg-white",
        variants === "under" && direction === "bottom" && "mt-4 mb-0 data-[state=closed]:mt-0",
        className
      ),
      ...props,
      onClick: isTarget ? () => onTargetClick : void 0,
      "data-slot": "accordion-trigger",
      children: [
        variants === "under" && /* @__PURE__ */ jsx("div", { className: "flex-1 h-[1px] bg-state-pending-02" }),
        /* @__PURE__ */ jsxs(
          "div",
          {
            className: cn(
              "header relative z-1 flex items-center gap-2 flex-none",
              variants === "under" ? (
                // ? 'under-title bg-transparent-black-16 justify-center gap-1 text-basic-04 px-4'
                "under-title justify-center gap-1 text-basic-04"
              ) : "justify-between flex-1 "
            ),
            children: [
              isChangeTitle ? /* @__PURE__ */ jsxs(Fragment, { children: [
                /* @__PURE__ */ jsx("span", { className: cn("open-title", isDesktop ? "body-lg" : "body-md"), children: openTitle }),
                /* @__PURE__ */ jsx("span", { className: cn("close-title", isDesktop ? "body-lg" : "body-md"), children: closeTitle })
              ] }) : openTitle ? /* @__PURE__ */ jsx("span", { className: cn(isDesktop ? "body-lg" : "body-md"), children: openTitle }) : children,
              renderHeaderIcon()
            ]
          }
        ),
        variants === "under" && /* @__PURE__ */ jsx("div", { className: "flex-1 h-[1px] bg-state-pending-02" })
      ]
    }
  ) }) });
});
AccordionTrigger.displayName = AccordionTrigger$1.displayName;
const AccordionContent = React.forwardRef(({ className, children, ...props }, ref) => {
  const { direction, variants } = React.useContext(AccordionContext);
  return /* @__PURE__ */ jsx(
    AccordionContent$1,
    {
      ref,
      className: cn(
        "overflow-hidden text-sm",
        direction === "bottom" ? "order-1 data-[state=open]:animate-accordion-down-up data-[state=closed]:animate-accordion-up-down origin-bottom" : "order-2 data-[state=closed]:animate-accordion-up data-[state=open]:rounded-t-none data-[state=open]:animate-accordion-down origin-top",
        variants === "under" ? "border-none p-0" : "border-t border-t-basic-04 p-5 bg-basic-background-07"
      ),
      ...props,
      children: /* @__PURE__ */ jsx("div", { className, children })
    }
  );
});
AccordionContent.displayName = AccordionContent$1.displayName;

export { Accordion, AccordionContent, AccordionItem, AccordionTrigger };
